/**
 * Component Card
 *
 * Displays a single optional component with install/uninstall actions.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';

import { canRunComponent, formatSize, getStatusBadge } from '../../services/componentService';
import type {
  ComponentInfo,
  HardwareCapabilities,
  InstallProgress,
} from '../../services/componentService';

interface ComponentCardProps {
  component: ComponentInfo;
  hardware: HardwareCapabilities | null;
  progress?: InstallProgress;
  onInstall: () => void;
  onUninstall: () => void;
  onDismissError?: () => void;
  onFetchLog?: () => Promise<string>;
  onManageModels?: () => void;
}

export const ComponentCard: React.FC<ComponentCardProps> = ({
  component,
  hardware,
  progress,
  onInstall,
  onUninstall,
  onDismissError,
  onFetchLog,
  onManageModels,
}) => {
  const { t } = useTranslation();
  const { dialogProps, confirm } = useConfirmDialog();
  const [showLog, setShowLog] = useState(false);
  const [logContent, setLogContent] = useState('');
  const [logLoading, setLogLoading] = useState(false);
  const [logCopied, setLogCopied] = useState(false);

  const isIndeterminate = !!progress && progress.step === 'installing' && progress.progress === 0;

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (!isIndeterminate) {
      setElapsedSeconds(0);
      return;
    }
    const interval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [isIndeterminate]);

  const formatElapsed = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const isCompatible = hardware ? canRunComponent(hardware, component.id) : true;
  const isInstalled =
    component.status === 'installed' ||
    component.status === 'running' ||
    component.status === 'stopped';
  const isInstalling =
    component.status === 'installing' ||
    (!!progress &&
      progress.step !== 'complete' &&
      progress.step !== 'error' &&
      progress.step !== 'idle');

  const statusBadge = getStatusBadge(component.status);

  const getIcon = () => {
    switch (component.icon) {
      case 'brain':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        );
      case 'image':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        );
      case 'mic':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
            />
          </svg>
        );
      case 'languages':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
            />
          </svg>
        );
      case 'volume-2':
      case 'volume-1':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
            />
          </svg>
        );
    }
  };

  return (
    <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 bg-white dark:bg-secondary-800">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400">
            {getIcon()}
          </div>
          <div>
            <h3 className="font-semibold text-secondary-900 dark:text-secondary-100">
              {component.name}
            </h3>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              {component.description}
            </p>
          </div>
        </div>
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            statusBadge.variant === 'success'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
              : statusBadge.variant === 'info'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                : statusBadge.variant === 'warning'
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                  : statusBadge.variant === 'error'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    : 'bg-secondary-100 text-secondary-800 dark:bg-secondary-900/30 dark:text-secondary-300'
          }`}
        >
          {statusBadge.text}
        </span>
      </div>

      {/* Requirements & VRAM Badge */}
      <div className="flex flex-wrap gap-4 text-sm text-secondary-600 dark:text-secondary-400 mb-3">
        <span>
          {t('additionalInstallations.size', 'Size')}: {formatSize(component.sizeMb)}
        </span>
        {component.requirements.ram_gb && (
          <span>
            {t('additionalInstallations.ram', 'RAM')}: {component.requirements.ram_gb}GB+
          </span>
        )}
        {component.requirements.vram_gb && (
          <span>
            {t('additionalInstallations.vram', 'VRAM')}: {component.requirements.vram_gb}GB+
          </span>
        )}
      </div>

      {/* Compatibility warning - only show for non-installed components */}
      {!isCompatible && !isInstalled && (
        <div className="mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded text-sm text-amber-700 dark:text-amber-300">
          {t(
            'additionalInstallations.incompatibleWarning',
            'Your system may not meet the requirements for this component.'
          )}
        </div>
      )}

      {/* Progress bar */}
      {progress &&
        progress.step !== 'complete' &&
        progress.step !== 'error' &&
        progress.step !== 'idle' && (
          <div className="mb-3">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-secondary-600 dark:text-secondary-400">
                {progress.message || progress.step}
              </span>
              <span className="text-secondary-600 dark:text-secondary-400">
                {isIndeterminate
                  ? formatElapsed(elapsedSeconds)
                  : `${Math.round(progress.progress)}%`}
              </span>
            </div>
            <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-2 overflow-hidden">
              {isIndeterminate ? (
                <div className="h-2 w-1/3 rounded-full bg-primary-600 animate-progress-indeterminate" />
              ) : (
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress.progress}%` }}
                />
              )}
            </div>
          </div>
        )}

      {/* Error panel */}
      {progress && progress.step === 'error' && (
        <div className="mb-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 overflow-hidden">
          <div className="p-3">
            <div className="flex items-start gap-2">
              <svg
                className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-red-800 dark:text-red-300">
                  {t('additionalInstallations.installErrorTitle', 'Installation Failed')}
                </h4>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                  {t(
                    'additionalInstallations.installErrorDescription',
                    'Something went wrong during the installation. This can happen due to network issues, missing permissions, or insufficient disk space.'
                  )}
                </p>
                <p className="text-xs text-red-600 dark:text-red-500 mt-2">
                  {t(
                    'additionalInstallations.installErrorContact',
                    'If the problem persists, please contact support with the installation log.'
                  )}
                </p>
              </div>
              {/* Dismiss button */}
              {onDismissError && (
                <button
                  onClick={onDismissError}
                  className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1"
                  aria-label="Dismiss"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 mt-3">
              {onFetchLog && (
                <button
                  onClick={async () => {
                    if (showLog) {
                      setShowLog(false);
                      return;
                    }
                    setLogLoading(true);
                    const log = await onFetchLog();
                    setLogContent(log);
                    setLogLoading(false);
                    setShowLog(true);
                  }}
                  disabled={logLoading}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                >
                  <svg
                    className="w-3.5 h-3.5 mr-1.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  {logLoading
                    ? '...'
                    : showLog
                      ? t('additionalInstallations.hideDetails', 'Hide Details')
                      : t('additionalInstallations.installErrorViewLogs', 'View Logs')}
                </button>
              )}
              {onFetchLog && showLog && logContent && (
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(logContent);
                    setLogCopied(true);
                    setTimeout(() => setLogCopied(false), 2000);
                  }}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                >
                  <svg
                    className="w-3.5 h-3.5 mr-1.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  {logCopied
                    ? t(
                        'additionalInstallations.installErrorLogsCopied',
                        'Logs copied to clipboard'
                      )
                    : t('additionalInstallations.installErrorCopyLogs', 'Copy Logs')}
                </button>
              )}
              <button
                onClick={onInstall}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
              >
                {t('additionalInstallations.installErrorRetry', 'Retry Installation')}
              </button>
            </div>
          </div>

          {/* Log viewer */}
          {showLog && logContent && (
            <div className="border-t border-red-200 dark:border-red-800 bg-black/50 p-3 max-h-64 overflow-y-auto">
              <pre className="text-xs text-red-200 dark:text-red-300 whitespace-pre-wrap font-mono leading-relaxed">
                {logContent}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Actions - hide Install button when error panel is showing (it has Retry) */}
      <div className="flex flex-wrap gap-2 mt-4">
        {isInstalled ? (
          <>
            {onManageModels && (
              <button
                onClick={onManageModels}
                className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-600 dark:border-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                {t('additionalInstallations.manageModels', 'Manage Models')}
              </button>
            )}
            <button
              onClick={async () => {
                const confirmed = await confirm({
                  title: t('additionalInstallations.uninstallTitle', 'Uninstall Component'),
                  message: t(
                    'additionalInstallations.confirmUninstall',
                    'Are you sure you want to uninstall this component?'
                  ),
                  confirmText: t('additionalInstallations.uninstall', 'Uninstall'),
                  cancelText: t('common.cancel', { defaultValue: 'Cancel' }),
                  variant: 'danger',
                });
                if (confirmed) {
                  onUninstall();
                }
              }}
              className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors min-h-[44px]"
            >
              {t('additionalInstallations.uninstall', 'Uninstall')}
            </button>
          </>
        ) : (
          progress?.step !== 'error' && (
            <button
              onClick={onInstall}
              disabled={!isCompatible || isInstalling}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                isCompatible && !isInstalling
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-secondary-300 dark:bg-secondary-600 text-secondary-500 dark:text-secondary-400 cursor-not-allowed'
              }`}
            >
              {isInstalling
                ? t('additionalInstallations.installing', 'Installing...')
                : t('additionalInstallations.install', 'Install')}
            </button>
          )
        )}
        {component.docsUrl && (
          <a
            href={component.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-sm font-medium text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            {t('additionalInstallations.learnMore', 'Learn More')}
          </a>
        )}
      </div>
      <ConfirmDialog {...dialogProps} />
    </div>
  );
};

export default ComponentCard;
